import React from "react";
import { ShieldWarning, TrendUp, TrendDown, Users } from "@phosphor-icons/react";

const TONE = {
  Calm: "text-emerald-300 border-emerald-500/30",
  Alert: "text-amber-300 border-amber-500/30",
  Dangerous: "text-orange-300 border-orange-500/30",
  Critical: "text-red-300 border-red-500/40",
};
const Pill = ({ label }) => (
  <span className={`rounded-full border px-2 py-0.5 text-xs ${TONE[label] || "text-ff-muted border-ff-brass/30"}`}>{label}</span>
);

export default function ThreatDebrief({ debrief }) {
  if (!debrief) return null;
  if (!debrief.available) {
    return (
      <div className="ff-panel rounded-md p-4" data-testid="threat-debrief-unavailable">
        <h3 className="flex items-center gap-2 font-display text-lg text-ff-brass"><ShieldWarning size={18} weight="duotone" /> Threat Debrief</h3>
        <p className="mt-1 text-sm text-ff-muted">{debrief.message || "Debrief unavailable for this expedition."}</p>
      </div>
    );
  }
  const d = debrief;
  return (
    <div className="ff-panel rounded-md p-4" data-testid="threat-debrief">
      <h3 className="flex items-center gap-2 font-display text-lg text-ff-gold"><ShieldWarning size={18} weight="duotone" /> Threat Debrief</h3>
      <p className="mt-0.5 text-[11px] text-ff-muted" data-testid="threat-debrief-note">{d.note}</p>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm" data-testid="threat-debrief-summary">
        <span className="text-ff-muted">Start</span><Pill label={d.starting_threat.label} />
        <span className="text-ff-muted">·  Peak</span><span data-testid="threat-debrief-peak"><Pill label={d.peak_threat.label} /></span>
        <span className="text-ff-muted">·  Final</span><span data-testid="threat-debrief-final"><Pill label={d.final_threat.label} /></span>
      </div>

      {d.largest_spike && (
        <div className="mt-3 flex items-start gap-2 rounded-sm border border-orange-500/25 bg-orange-500/5 p-2" data-testid="threat-debrief-spike">
          <TrendUp size={16} weight="bold" className="mt-0.5 text-orange-300" />
          <div className="text-sm text-ff-parchment">
            <b>{d.largest_spike.from_label} → {d.largest_spike.to_label}.</b> {d.largest_spike.description}
            {d.largest_spike.choice && <span className="text-ff-muted"> Choice: “{d.largest_spike.choice}”.</span>}
          </div>
        </div>
      )}

      {(d.de_escalations || []).length > 0 && (
        <div className="mt-2 flex items-start gap-2 text-sm text-ff-muted" data-testid="threat-debrief-deescalation">
          <TrendDown size={16} weight="bold" className="mt-0.5 text-emerald-300" />
          <span>{d.de_escalations.length} steadying moment{d.de_escalations.length > 1 ? "s" : ""} eased the Threat back down.</span>
        </div>
      )}

      {(d.crew_central || []).length > 0 && (
        <div className="mt-2 flex items-center gap-2 text-sm" data-testid="threat-debrief-crew">
          <Users size={16} weight="duotone" className="text-ff-brass" />
          <span className="text-ff-parchment">Central crew: {d.crew_central.join(", ")}</span>
        </div>
      )}

      {(d.timeline || []).length > 0 && (
        <div className="mt-3" data-testid="threat-debrief-timeline">
          <div className="mb-1 text-[11px] uppercase tracking-widest text-ff-brass">Threat path</div>
          <div className="flex flex-wrap items-center gap-1.5">
            {d.timeline.map((t, i) => (
              <span key={i} className="flex items-center gap-1">
                {i > 0 && <span className="text-ff-muted/60">{t.direction === "rise" ? "↗" : t.direction === "ease" ? "↘" : "→"}</span>}
                <Pill label={t.level_label} />
              </span>
            ))}
          </div>
        </div>
      )}
      {(d.model_versions || []).length > 0 && (
        <p className="mt-2 text-[10px] text-ff-muted/70">Model: {d.model_versions.join(", ")} (historical snapshots preserved)</p>
      )}
    </div>
  );
}
